import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { VoterProfileType } from 'src/@types/voterSearch';
import Label from 'src/components/label';
import { PartyAvatar } from 'src/components/party-avatar';
import { getPartyAbbreviation } from 'src/utils/getPartyAbbreviation';
import { getPartyColor } from 'src/utils/getPartyColor';
import { toProperCase } from 'src/utils/toProperCase';

type VoterListItemData = Pick<
  VoterProfileType,
  | 'first_name'
  | 'middle_name'
  | 'last_name'
  | 'name_suffix'
  | 'sex'
  | 'age'
  | 'registered_party_roll_up'
  | 'registration_address_1'
  | 'registration_address_2'
  | 'registration_address_city'
  | 'registration_address_state'
  | 'registration_address_zip_5'
  | 'mailing_address_1'
  | 'mailing_address_2'
  | 'mailing_address_city'
  | 'mailing_address_state'
  | 'mailing_address_zip_5'
>;

type VoterListItemProps = {
  voter: VoterListItemData;
  showAddress?: boolean;
  avatarSize?: 'small' | 'default';
};

export function VoterListItem({ voter, showAddress, avatarSize = 'default' }: VoterListItemProps) {
  const theme = useTheme();

  const voterName = toProperCase(
    voter.first_name +
      (voter.middle_name ? ' ' + voter.middle_name : '') +
      ' ' +
      voter.last_name +
      (voter.name_suffix ? ', ' + voter.name_suffix : '')
  );

  const registrationAddress = toProperCase(
    (voter.registration_address_1 ? voter.registration_address_1 + ', ' : '') +
      (voter.registration_address_2 ? voter.registration_address_2 + ', ' : '') +
      (voter.registration_address_city ? voter.registration_address_city + ', ' : '') +
      (voter.registration_address_state ? voter.registration_address_state + ' ' : '') +
      (voter.registration_address_zip_5 ? voter.registration_address_zip_5 : '')
  );

  const mailingAddress = toProperCase(
    (voter.mailing_address_1 ? voter.mailing_address_1 + ', ' : '') +
      (voter.mailing_address_2 ? voter.mailing_address_2 + ', ' : '') +
      (voter.mailing_address_city ? voter.mailing_address_city + ', ' : '') +
      (voter.mailing_address_state ? voter.mailing_address_state + ' ' : '') +
      (voter.mailing_address_zip_5 ? voter.mailing_address_zip_5 : '')
  );

  const fullAddress = registrationAddress || mailingAddress || 'No Address';

  const voterColor = getPartyColor(voter.registered_party_roll_up || '');
  const palette = theme.palette[voterColor] || theme.palette.primary;

  const sexLabel = voter.sex ? voter.sex.charAt(0).toUpperCase() : '?';
  const ageLabel = voter.age ? `${voter.age}yo` : '?';

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <PartyAvatar
          name={`${voter.first_name} ${voter.last_name}`}
          party={voter.registered_party_roll_up || ''}
          sx={
            avatarSize === 'small'
              ? { width: 28, height: 28, fontSize: 10, lineHeight: 22 }
              : undefined
          }
        />
        <Stack direction="column">
          <Typography variant="subtitle2">{voterName}</Typography>
          {showAddress && (
            <Typography variant="caption" color="textSecondary">
              {fullAddress}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <Label variant="outlined" color="default">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
            minWidth={42}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: 999, backgroundColor: palette.main }} />
            <Box>{getPartyAbbreviation(voter.registered_party_roll_up || '')}</Box>
          </Stack>
        </Label>
        <Label variant="outlined" color="default" sx={{ flexShrink: 0, minWidth: 32 }}>
          {sexLabel}
        </Label>
        <Label
          variant="outlined"
          color="default"
          sx={{ flexShrink: 0, minWidth: 54, textTransform: 'lowercase' }}
        >
          {ageLabel}
        </Label>
      </Stack>
    </Stack>
  );
}
